import { zodResolver } from "@hookform/resolvers/zod";
import { useNavigate } from "@tanstack/react-router";
import { Controller, type SubmitHandler, useForm } from "react-hook-form";
import { z } from "zod";

import type { RouterOutputs } from "@blis/trpc/react";
import { trpc } from "@blis/trpc/react";

import { Link } from "@/components/Link";
import { Button } from "@/components/ui/button";
import { Checkbox } from "@/components/ui/checkbox";
import {
  Description,
  FieldError,
  Input,
  Label,
  TextField,
} from "@/components/ui/field";
import { NumberField, NumberInput } from "@/components/ui/number-field";
import {
  Select,
  SelectItem,
  SelectList,
  SelectTrigger,
} from "@/components/ui/select";
import { Separator } from "@/components/ui/separator";
import { mutationResponse } from "@/utils/trpc";
import { AdminForm } from "../AdminForm";

const formSchema = z.discriminatedUnion("type", [
  z.object({
    name: z.string().min(1, "Required"),
    type: z.literal("NETWORK"),
    address: z.string().min(1, "IP address is required"),
    port: z.number().int().min(1).max(65535),
    label: z.string().optional(),
    enabled: z.boolean(),
  }),
  z.object({
    name: z.string().min(1, "Required"),
    type: z.literal("USB"),
    usbVendorId: z.string().min(1, "USB vendor ID is required"),
    usbProductId: z.string().min(1, "USB product ID is required"),
    label: z.string().optional(),
    enabled: z.boolean(),
  }),
]);

type FormValues = z.infer<typeof formSchema>;
type PrinterDetail = RouterOutputs["printers"]["get"];

const typeOptions = [
  { label: "Network (TCP)", value: "NETWORK" },
  { label: "USB", value: "USB" },
];

export function PrinterDetailForm({ printer }: { printer?: PrinterDetail }) {
  const create = trpc.printers.create.useMutation();
  const update = trpc.printers.update.useMutation();
  const navigate = useNavigate();

  const { control, handleSubmit, watch } = useForm<FormValues>({
    defaultValues: {
      name: printer?.name ?? "",
      type: (printer?.type as "NETWORK" | "USB") ?? "NETWORK",
      address: printer?.address ?? "",
      port: printer?.port ?? 9100,
      usbVendorId: printer?.usbVendorId ?? "",
      usbProductId: printer?.usbProductId ?? "",
      label: printer?.label ?? "",
      enabled: printer?.enabled ?? true,
    },
    resolver: zodResolver(formSchema),
  });

  const printerType = watch("type");

  const onSubmit: SubmitHandler<FormValues> = (values) => {
    if (printer) {
      update.mutate(
        { id: printer.id, ...values },
        mutationResponse({
          errorTitle: "Error updating printer",
          successMessage: "Printer updated",
        }),
      );
    } else {
      create.mutate(
        values,
        mutationResponse({
          errorTitle: "Error adding printer",
          successMessage: "Printer added",
          onSuccess(data) {
            navigate({
              to: "/admin/printers/$id/edit",
              params: { id: data.id },
            });
          },
        }),
      );
    }
  };

  return (
    <AdminForm onSubmit={handleSubmit(onSubmit)}>
      <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
        <Controller
          control={control}
          name="name"
          render={({ field, fieldState: { error } }) => (
            <TextField
              {...field}
              value={field.value ?? ""}
              isInvalid={!!error}
              validationBehavior="aria"
            >
              <Label>Name</Label>
              <Input />
              <Description>A recognizable name for this printer</Description>
              <FieldError>{error?.message}</FieldError>
            </TextField>
          )}
        />

        <Controller
          control={control}
          name="type"
          render={({ field: { ref, ...field }, fieldState: { error } }) => (
            <Select
              {...field}
              selectedKey={field.value}
              onSelectionChange={(value) => field.onChange(value)}
              isInvalid={!!error}
              validationBehavior="aria"
            >
              <Label>Connection type</Label>
              <SelectTrigger />
              <SelectList items={typeOptions}>
                {(item) => (
                  <SelectItem id={item.value}>{item.label}</SelectItem>
                )}
              </SelectList>
              <FieldError>{error?.message}</FieldError>
            </Select>
          )}
        />

        {printerType === "NETWORK" && (
          <>
            <Controller
              control={control}
              name="address"
              render={({ field, fieldState: { error } }) => (
                <TextField
                  {...field}
                  value={field.value ?? ""}
                  isInvalid={!!error}
                  validationBehavior="aria"
                >
                  <Label>IP address</Label>
                  <Input placeholder="192.168.1.100" />
                  <FieldError>{error?.message}</FieldError>
                </TextField>
              )}
            />

            <Controller
              control={control}
              name="port"
              render={({ field, fieldState: { error } }) => (
                <NumberField
                  name={field.name}
                  value={field.value ?? 9100}
                  onChange={(val) =>
                    field.onChange(Number.isNaN(val) ? 9100 : val)
                  }
                  onBlur={field.onBlur}
                  isInvalid={!!error}
                  validationBehavior="aria"
                  minValue={1}
                  maxValue={65535}
                >
                  <Label>Port</Label>
                  <NumberInput />
                  <Description>Default: 9100</Description>
                  <FieldError>{error?.message}</FieldError>
                </NumberField>
              )}
            />
          </>
        )}

        {printerType === "USB" && (
          <>
            <Controller
              control={control}
              name="usbVendorId"
              render={({ field, fieldState: { error } }) => (
                <TextField
                  {...field}
                  value={field.value ?? ""}
                  isInvalid={!!error}
                  validationBehavior="aria"
                >
                  <Label>USB vendor ID</Label>
                  <Input placeholder="0a5f" />
                  <Description>Hex vendor ID (e.g. 0a5f for Zebra)</Description>
                  <FieldError>{error?.message}</FieldError>
                </TextField>
              )}
            />

            <Controller
              control={control}
              name="usbProductId"
              render={({ field, fieldState: { error } }) => (
                <TextField
                  {...field}
                  value={field.value ?? ""}
                  isInvalid={!!error}
                  validationBehavior="aria"
                >
                  <Label>USB product ID</Label>
                  <Input placeholder="00a8" />
                  <FieldError>{error?.message}</FieldError>
                </TextField>
              )}
            />
          </>
        )}

        <Controller
          control={control}
          name="label"
          render={({ field, fieldState: { error } }) => (
            <TextField
              {...field}
              value={field.value ?? ""}
              isInvalid={!!error}
              validationBehavior="aria"
            >
              <Label>Lab label</Label>
              <Input placeholder="Hematology" />
              <Description>
                Optional label to help identify which lab this printer belongs
                to
              </Description>
              <FieldError>{error?.message}</FieldError>
            </TextField>
          )}
        />

        <Controller
          control={control}
          name="enabled"
          render={({ field }) => (
            <Checkbox isSelected={field.value} onChange={field.onChange}>
              Enabled
            </Checkbox>
          )}
        />
      </div>

      <Separator />
      <div className="flex justify-end gap-2">
        <Link to="/admin/printers" variant="outline" asButton>
          Cancel
        </Link>
        <Button type="submit" isPending={create.isPending || update.isPending}>
          Save
        </Button>
      </div>
    </AdminForm>
  );
}
